export const FLOW_STATUS = {
  NEW: "new",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
} as const;

export type FlowStatus = (typeof FLOW_STATUS)[keyof typeof FLOW_STATUS];

export const FORM_FIELD = {
  WEBSITE_URL: "Website URL",
  OPP_NUMBER: "Opportunity number",
  SEARCH_MODE: "Search on this specific page",
  DEMO_ENVIRONMENT: "Demo Environment",
  COMPANY_OVERVIEW: "Company Overview",
  CORPORATE_EMAIL_ADDRESS: "Corporate Email Address",
  PASSWORD: "Password",
  SECTOR_POSITIONING: "Sector Positioning",
  INDUSTRY: "Industry",
  SUB_INDUSTRY: "Focus Areas",
  COUNTRIES: "Operational Countries",
  SEARCH_ON_SPECIFIC_PAGE: "Search on this specific page",
  ENABLE_SNOWFLAKE: "Snowflake Semantic Assets",
  DISCOVERY_NOTES: "Discovery Notes",
  USE_CASE: "Use Case",
  ALWAYS_GENERATE_THOUGHTSPOT_LIVEBOARD: "Always generate ThoughtSpot Liveboard",
  GENERATE_SNOWFLAKE_SEMANTIC_VIEW: "Always Generate Snowflake Semantic view",
  ALWAYS_GENERATE_GENIE_SPACE: "Always Generate Genie Space",
  ALWAYS_GENERATE_AIBI_DASHBOARD: "Always Generate AI/BI dashboard",
} as const;

export type FormField = (typeof FORM_FIELD)[keyof typeof FORM_FIELD];

export const PLACEHOLDERS = {
  WEBSITE_URL: "https://www.blitzdemos.ai/",
  OPP_NUMBER: "Type...",
  EMAIL: "E.x. name@blitzdemos.com",
  PASSWORD: "Enter your password",
} as const;

export const DESCRIPTIONS = {
  DATASET_GENERATION_NOTE: "Add the company's website (or a specific page) to get started.Simply add the company's website address (or a specific web page). ",
} as const;

export const BUTTON_LABLE = {
  START_DATASET_GENERATION: "Blitz it",
  NEXT: "Next",
  CONTINUE: "Continue",
  GENERATE: "Generate",
} as const;

export const COGNITO_CONFIG = {
  authority: import.meta.env.VITE_CONFIG_AUTHORITY,
  client_id: import.meta.env.VITE_CONFIG_CLIENT_ID,
  redirect_uri: window.location.origin,
  response_type: import.meta.env.VITE_CONFIG_RESPONSE_TYPE,
  scope: import.meta.env.VITE_CONFIG_SCOPE,
};

const isDevelopment = import.meta.env.DEV;

const ENVIRONMENT_REDIRECT_URI = isDevelopment ? COGNITO_CONFIG.redirect_uri : encodeURIComponent(COGNITO_CONFIG.redirect_uri);

export const COGNITO_LOGIN_URL = `${COGNITO_CONFIG.authority}/login?client_id=${COGNITO_CONFIG.client_id}&response_type=${COGNITO_CONFIG.response_type}&redirect_uri=${ENVIRONMENT_REDIRECT_URI}`;
export const COGNITO_LOGOUT_URL = `${COGNITO_CONFIG.authority}/logout?client_id=${COGNITO_CONFIG.client_id}&logout_uri=${ENVIRONMENT_REDIRECT_URI}&redirect_uri=${ENVIRONMENT_REDIRECT_URI}&response_type=${COGNITO_CONFIG.response_type}`;
export const COGNITO_SIGNUP_URL = `${COGNITO_CONFIG.authority}/signup?client_id=${COGNITO_CONFIG.client_id}&response_type=${COGNITO_CONFIG.response_type}&redirect_uri=${ENVIRONMENT_REDIRECT_URI}`;

export const MockupLabStatus = {
  Draft: "draft",
  Published: "published",
  Archived: "archived",
  Active: "active",
  Disabled: "disabled",
} as const;

export type MockupLabStatus = (typeof MockupLabStatus)[keyof typeof MockupLabStatus];

export const STATUS_META = {
  [MockupLabStatus.Draft]: { label: "Draft", color: "bg-primary-10 text-primary-100" },
  [MockupLabStatus.Published]: { label: "Published", color: "bg-success-10 text-success-100" },
  [MockupLabStatus.Archived]: { label: "Archived", color: "bg-dark-10 text-dark-60" },
  [MockupLabStatus.Active]: { label: "Active", color: "bg-success-10 text-success-100" },
  [MockupLabStatus.Disabled]: { label: "Disabled", color: "bg-primary-10 text-neutral-100" },
} as const;

export const LABEL_MAPPER: Record<string, Record<string, string>> = {
  "mockup-lab": {
    WEBSITE_URL: "Prospect Website Url",
    OPP_NUMBER: "Salesforce Opportunity number",
    SEARCH_ON_SPECIFIC_PAGE: "Search on this specific page",
  },
  default: {
    WEBSITE_URL: "Website URL",
    OPP_NUMBER: "Opportunity Number",
    SEARCH_ON_SPECIFIC_PAGE: "Search on this specific page",
    FORMULA_SET: "Output",
  },
};

export const DEMO_LAB_OUTPUT_FIELD_NAME = "output" as const;
export const DATABRICKS_FLOW_TYPE_FIELD_NAME = "databricks_flow_type" as const;
export const DEFAULT_DATABRICKS_FLOW_TYPE = "demo_genie_ai_bi" as const;

export const DATABRICKS_FLOW_TYPE_LABELS = {
  demo_genie_ai_bi: "Demo Genie + AI/BI",
  lakebase_lakehouse_ai_bi: "Lakebase + Lakehouse + AI/BI",
} as const;

export const DATABRICKS_FLOW_TYPE_OPTIONS = [
  {
    value: "demo_genie_ai_bi",
    label: DATABRICKS_FLOW_TYPE_LABELS.demo_genie_ai_bi,
    subtitle: "Original flow",
    description:
      "Metrics View + Genie Space + AI/BI Dashboard. Best for quick, opinionated demos.",
  },
  {
    value: "lakebase_lakehouse_ai_bi",
    label: DATABRICKS_FLOW_TYPE_LABELS.lakebase_lakehouse_ai_bi,
    subtitle: "Full stack flow",
    description:
      "End-to-end architecture across Lakebase, Lakehouse and AI/BI. Use when prospect needs the full picture.",
  },
] as const;

export const menuOptions = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard", note: "*Custom page", iconSize: 16 },
  {
    id: "my_reports",
    label: "My Reports",
    icon: "my_reports",
    note: "*Liveboard you have generated before",
    iconSize: 18,
  },
  { id: "spotter", label: "Spotter", icon: "spotter", note: "", iconSize: 22 },
];

export const actionOptions = [
  { label: "Download", value: "download" },
  { label: "Download as PNG", value: "downloadAsPng" },
  { label: "Download as CSV", value: "downloadAsCSV" },
  { label: "Drill down", value: "DRILL" },
  { label: "Share", value: "share" },
  { label: "Make a copy", value: "makeACopy" },
  { label: "Edit TML", value: "tml" },
] as const;

export const THOUGHTSPOT_FIELDS = {
  org_id: "org_id",
  org_name: "org_name",
  data_source: "data_source",
  username: "username",
  secret_key: "secret_key",
  cluster_url: "cluster_url",
  connection_name: "connection_name",
} as const;

export type ThoughtspotFieldKeys = (typeof THOUGHTSPOT_FIELDS)[keyof typeof THOUGHTSPOT_FIELDS];

export type TFormData = {
  [key in ThoughtspotFieldKeys]?: string | number | { id: string | number; name: string };
} & { id?: number; data_source_id?: number };

export const SNOWFLAKE_FIELDS = {
  ACCOUNT_ID: "account_id",
  ACCOUNT_URL: "account_url",
  REGION: "region",
  USERNAME: "username",
  TOKEN: "token",
  ACCOUNT_ROLE: "account_role",
  WAREHOUSE_NAME: "warehouse_name",
  DB_NAME: "db_name",
  SCHEMA_NAME: "schema_name",
  IS_ACTIVE: "is_active",
  CONNECTION_NAME: "connection_name",
} as const;

export type SnowflakeFieldKeys = (typeof SNOWFLAKE_FIELDS)[keyof typeof SNOWFLAKE_FIELDS];

export type TFormDataSnowflake = {
  [key in SnowflakeFieldKeys]?: string;
} & { id?: number; warehouse_id?: number };

export const S3_CONNECTION_TYPES = [
  { label: "Access Key", value: "access_key" },
  { label: "IAM Role", value: "iam_role" }
] as const;

export type S3ConnectionType = (typeof S3_CONNECTION_TYPES)[number]["value"];

export const BUECKET_CONNECTION_FEILDS = {
  BUCKET_NAME: "bucket_name",
  AWS_ACCESS_KEY_ID: "aws_access_key_id",
  AWS_SECRET_ACCESS_KEY: "aws_secret_access_key",
  AWS_SESSION_TOKEN: "aws_session_token",
  IAM_ROLE_ARN: "iam_role_arn",
  EXTERNAL_ID: "external_id",
} as const;

export type S3ConnectionFieldKeys = (typeof BUECKET_CONNECTION_FEILDS)[keyof typeof BUECKET_CONNECTION_FEILDS];

export type TFormDataS3 = {
  [key in S3ConnectionFieldKeys]?: string;
} & { auth_type?: "access_key" | "iam_role"; id?: number };

export const DATABRICKS_CONNECTION_NAME_STORAGE_KEY = "databricks_connection_name";

export const DATABRICKS_AUTH_CODE_COOKIE = "authCode";

export const DEFAULT_DATABRICKS_CONNECTION_NAME = "Databricks connection";

export const DATABRICKS_FIELDS = {
  ACCOUNT_ID: "account_id",
  ACCOUNT_URL: "account_url",
  WORKSPACE_URL: "workspace_url",
  REGION: "region",
  SERVICE_PRINCIPAL: "service_principal",
  SERVICE_PRINCIPAL_APP_ID: "service_principal_app_id",
  TOKEN: "token",
  USER_GROUP: "user_group",
  DETAILES_ID: "details_id",
  WAREHOUSE_NAME: "warehouse_name",
  WAREHOUSE_ID: "warehouse_id",
  CATELOG_NAME: "catalog_name",
  CLIENT_ID: "client_id",
  IS_ACTIVE: "is_active",
  CONNECTION_NAME: "connection_name",
  PAT_TOKEN: "pat_token",
  SECRET_ID: "secret_id",
  AUTH_TYPE: "auth_type",
} as const;

export type DatabricksFieldKeys = (typeof DATABRICKS_FIELDS)[keyof typeof DATABRICKS_FIELDS];

export type TFormDataDatabricks = {
  port?: number;
} & {
  [key in Exclude<DatabricksFieldKeys, "port">]?: string;
} & {
  id?: number;
  cluster_id?: number;
};

export const MODEL_OPTIONS = [
  { label: "gpt-4o", value: "gpt-4o" },
  { label: "o3", value: "o3" },
  { label: "gpt-4.1", value: "gpt-4.1" },
  { label: "gpt-4.1-2025-04-14", value: "gpt-4.1-2025-04-14" },
  { label: "gpt-4o-2024-08-06", value: "gpt-4o-2024-08-06" },
  { label: "gpt-4o-mini", value: "gpt-4o-mini" },
  { label: "gpt-5-mini", value: "gpt-5-mini" },
  { label: "gpt-5", value: "gpt-5" },
  { label: "gpt-5-2025-08-07", value: "gpt-5-2025-08-07" },
  { label: "gpt-4", value: "gpt-4" },
  { label: "o4-mini", value: "o4-mini" },
  { label: "o4-mini-2025-04-16", value: "o4-mini-2025-04-16" },
  { label: "o3-2025-04-16", value: "o3-2025-04-16" },
  { label: "text-embedding-3-large", value: "text-embedding-3-large" },
  { label: "gpt-5-chat-latest", value: "gpt-5-chat-latest" },
  { label: "text-embedding-3-small", value: "text-embedding-3-small" },
  { label: "chatgpt-4o-latest", value: "chatgpt-4o-latest" },
  { label: "gpt-5-mini-2025-08-07", value: "gpt-5-mini-2025-08-07" },
  { label: "o3-mini-2025-01-31", value: "o3-mini-2025-01-31" },
  { label: "o3-mini", value: "o3-mini" },
  { label: "gpt-4.1-mini", value: "gpt-4.1-mini" },
  { label: "gpt-4.1-mini-2025-04-14", value: "gpt-4.1-mini-2025-04-14" },
  { label: "text-embedding-ada-002", value: "text-embedding-ada-002" },
];

export const REASONING_MODELS = [
    "o3",
    "o3-2025-04-16",
    "o3-mini",
    "o3-mini-2025-01-31",
    "o4-mini",
    "o4-mini-2025-04-16",
    "gpt-5",
    "gpt-5-2025-08-07",
    "gpt-5-mini",
    "gpt-5-mini-2025-08-07",
    "gpt-5-chat-latest"
];

export const RESERVED_STEP_KEYS = new Set(["file_search"]);

export const DEMO_LAB_BASE_PATH = "/demo-lab";

export const INTEGRATION_CREATE_CONNECTION_PATH = "/integration/create_new_connection";
export const INTEGRATION_CREATE_CONNECTION_CONFIG_PATH =
    "/integration/create_new_connection/config";

export const INTEGRATION_CONNECTION_TARGET_STORAGE_KEY = "integration_connection_target";
export const INTEGRATION_EDIT_CONNECTION_ID_STORAGE_KEY = "integration_edit_connection_id";
export const DATABRICKS_CONNECTION_FLOW_MODE_STORAGE_KEY = "databricks_connection_flow_mode";
export const DATABRICKS_CONNECTION_CONFIG_DRAFT_STORAGE_KEY = "databricks_connection_config_draft";

export const BI_PLATFORM_OPTIONS = [
    {
        id: "thoughtspot",
        name: "ThoughtSpot",
        icon: "thoughtspot",
    },
];

export const THOUGHTSPOT_OPTIONS = [
    {
        id: "thoughtspot",
        name: "ThoughtSpot",
        icon: "thoughtspot",
    },
];

export const DATABASE_PLATFORMS = [
  { id: "snowflake", name: "Snowflake", icon: "snowflake" },
  { id: "databricks", name: "Databricks", icon: "databricks" },
] as const;

export const DEMO_LAB_OUTPUT_PLATFORMS = [
  { id: "snowflake", name: "Snowflake", icon: "snowflake" },
  { id: "thoughtspot", name: "ThoughtSpot", icon: "thoughtspot" },
  { id: "databricks", name: "Databricks", icon: "databricks" },
] as const;
